// VER-218: invitee set-password page (the target of the invite email link).
// /console/invite/:token -> validate token -> set password -> accept (logs in) -> dashboard.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { App, Button, Card, Form, Input, Typography, Spin, Result } from "antd";
import { useSession } from "../console/useSession";

const { Title, Text } = Typography;

const errorMessage = (err) =>
  err?.response?.data?.error?.message || "Something went wrong. Please try again.";

export default function ConsoleInvite() {
  const { token } = useParams();
  const { validateInvite, acceptInvite } = useSession();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [state, setState] = useState("validating"); // validating | valid | invalid
  const [invite, setInvite] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Validate the invite token on load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await validateInvite(token);
        if (!cancelled) {
          setInvite(res.data.data);
          setState("valid");
        }
      } catch {
        if (!cancelled) setState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, validateInvite]);

  const handleSubmit = async ({ password }) => {
    setSubmitting(true);
    try {
      await acceptInvite(token, password);
      message.success("Welcome! Your account is set up.");
      navigate("/console", { replace: true });
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card style={{ width: 420 }}>
        {state === "validating" && (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Checking your invite…</Text>
            </div>
          </div>
        )}

        {state === "invalid" && (
          <Result
            status="warning"
            title="Invite not valid"
            subTitle="This invite is invalid, expired, or has already been used. Ask your admin to send a new one."
            extra={
              <Link to="/console/login">
                <Button>Go to sign in</Button>
              </Link>
            }
          />
        )}

        {state === "valid" && invite && (
          <>
            <Title level={3} style={{ marginBottom: 4 }}>
              Set your password
            </Title>
            <Text type="secondary">
              You've been invited to <b>{invite.accountName}</b> as <b>{invite.role}</b> (
              {invite.email}).
            </Text>
            <Form
              layout="vertical"
              onFinish={handleSubmit}
              style={{ marginTop: 16 }}
              disabled={submitting}
              requiredMark={false}
            >
              <Form.Item
                name="password"
                label="New password"
                hasFeedback
                rules={[
                  { required: true, message: "Enter a password" },
                  { min: 12, message: "At least 12 characters" },
                  { max: 64, message: "At most 64 characters" },
                ]}
              >
                <Input.Password
                  placeholder="12–64 characters"
                  size="large"
                  autoComplete="new-password"
                />
              </Form.Item>
              <Form.Item
                name="confirm"
                label="Confirm password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  { required: true, message: "Confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) return Promise.resolve();
                      return Promise.reject(new Error("Passwords don't match"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Re-enter password"
                  size="large"
                  autoComplete="new-password"
                />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
                Set password &amp; continue
              </Button>
            </Form>
          </>
        )}
      </Card>
    </div>
  );
}
